import { api } from "../../api";
import type { Person, PlanDetail, PlanItem, PlanSummary, ServiceType, SongDetail, SongSummary } from "../../../shared/types";

export type PlanListStatus = "active" | "draft" | "confirmed" | "archived" | "all";

export interface PlanWrite {
  title: string;
  serviceTypeId: string;
  startsAt: string;
  status?: "draft" | "confirmed" | "archived";
  notes?: string;
  publicNotes?: string;
  series?: string | null;
}

export interface ItemWrite {
  kind: PlanItem["kind"];
  title?: string;
  songId?: string | null;
  arrangementId?: string | null;
  keyName?: string | null;
  durationSeconds?: number;
  leaderPersonId?: string | null;
  description?: string;
}

export type ItemPatch = Partial<ItemWrite>;

export interface RehearsalWrite {
  name: string;
  startsAt: string;
  endsAt?: string | null;
  place?: string;
  notes?: string;
}

function planPath(planId: string, suffix = ""): string {
  return `/api/v1/plans/${encodeURIComponent(planId)}${suffix}`;
}

export function listServiceTypes(): Promise<ServiceType[]> {
  return api<ServiceType[]>("/api/v1/service-types");
}

export function listPlans(query: { status?: PlanListStatus; from?: string; to?: string } = {}): Promise<PlanSummary[]> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  const suffix = params.size ? `?${params}` : "";
  return api<PlanSummary[]>(`/api/v1/plans${suffix}`);
}

export function loadPlan(planId: string): Promise<PlanDetail> {
  return api<PlanDetail>(planPath(planId));
}

export function createPlan(body: PlanWrite): Promise<PlanDetail> {
  return api<PlanDetail>("/api/v1/plans", { method: "POST", body: JSON.stringify(body) });
}

export function updatePlan(planId: string, body: Partial<PlanWrite>): Promise<PlanDetail> {
  return api<PlanDetail>(planPath(planId), { method: "PATCH", body: JSON.stringify(body) });
}

export function archivePlan(planId: string): Promise<PlanDetail> {
  return api<PlanDetail>(planPath(planId, "/archive"), { method: "POST" });
}

export function restorePlan(planId: string): Promise<PlanDetail> {
  return api<PlanDetail>(planPath(planId, "/restore"), { method: "POST" });
}

export function duplicatePlan(planId: string, body: { startsAt: string; title?: string }): Promise<PlanDetail> {
  return api<PlanDetail>(planPath(planId, "/duplicate"), { method: "POST", body: JSON.stringify(body) });
}

export function addItem(planId: string, body: ItemWrite): Promise<PlanDetail> {
  return api<PlanDetail>(planPath(planId, "/items"), { method: "POST", body: JSON.stringify(body) });
}

export function updateItem(itemId: string, body: ItemPatch): Promise<PlanDetail> {
  return api<PlanDetail>(`/api/v1/plan-items/${encodeURIComponent(itemId)}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function deleteItem(itemId: string): Promise<PlanDetail> {
  return api<PlanDetail>(`/api/v1/plan-items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
}

export function reorderItems(planId: string, orderedIds: string[]): Promise<PlanDetail> {
  return api<PlanDetail>(planPath(planId, "/items/reorder"), { method: "POST", body: JSON.stringify({ orderedIds }) });
}

export function addRehearsal(planId: string, body: RehearsalWrite): Promise<PlanDetail> {
  return api<PlanDetail>(planPath(planId, "/rehearsals"), { method: "POST", body: JSON.stringify(body) });
}

export function assignPerson(positionId: string, personId: string): Promise<PlanDetail> {
  return api<PlanDetail>(`/api/v1/plans/positions/${encodeURIComponent(positionId)}/assignments`, {
    method: "POST",
    body: JSON.stringify({ personId }),
  });
}

export function respondToAssignment(assignmentId: string, status: "pending" | "accepted" | "declined", note: string): Promise<PlanDetail> {
  return api<PlanDetail>(`/api/v1/assignments/${encodeURIComponent(assignmentId)}/respond`, {
    method: "POST",
    body: JSON.stringify({ status, note }),
  });
}

export function removeAssignment(assignmentId: string): Promise<PlanDetail> {
  return api<PlanDetail>(`/api/v1/assignments/${encodeURIComponent(assignmentId)}`, { method: "DELETE" });
}

export function listPeople(query = ""): Promise<Person[]> {
  const params = new URLSearchParams({ limit: "100" });
  if (query.trim()) params.set("q", query.trim());
  return api<Person[]>(`/api/v1/people?${params}`);
}

export function searchSongs(query: string): Promise<SongSummary[]> {
  const params = new URLSearchParams({ limit: "20" });
  if (query.trim()) params.set("q", query.trim());
  return api<SongSummary[]>(`/api/v1/songs?${params}`);
}

export function loadSong(songId: string): Promise<SongDetail> {
  return api<SongDetail>(`/api/v1/songs/${encodeURIComponent(songId)}`);
}
